import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Grid from "@mui/material/Grid"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "./components/MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import PaymentLineRow from "./components/PaymentLineRow"
import AttachmentSlot from "./components/AttachmentSlot"
import SectionCard from "./components/SectionCard"
import { useRequestForm } from "./hooks/useRequestForm"
import {
  REGION_OPTIONS,
  REQUESTER_REGION_OPTIONS,
  URGENCY_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  DOC_TYPES,
} from "./constants"
import {
  useCreateProcurementRequestMutation,
  useGetBudgetCategoriesQuery,
  useGetDepartmentsQuery,
  useGetSuppliersQuery,
  useGetPosQuery,
  useLazyGetPoDetailQuery,
  useSubmitProcurementRequestMutation,
  useUploadProcurementDocumentMutation,
} from "shared/redux/endpoints/procurement"
import { selectProcurement } from "shared/redux/slices/authSlice"

const CreateRequestContainer = () => {
  const navigate = useNavigate()
  const procurement = useSelector(selectProcurement)

  const form = useRequestForm({
    header: { requesterRegion: procurement.defaultRegion || "" },
  })
  const {
    header,
    lines,
    errors,
    isValid,
    setHeaderField,
    setHeader,
    updateLine,
    addLine,
    duplicateLine,
    removeLine,
    totalAmountPR,
    totalAmountPO,
    totalsDiffer,
    buildBody,
  } = form

  const [pendingDocs, setPendingDocs] = useState({}) // { docType: File }

  const { data: categories } = useGetBudgetCategoriesQuery({ activeOnly: true })
  const { data: departments } = useGetDepartmentsQuery()
  const { data: suppliers } = useGetSuppliersQuery({})
  const { data: pos } = useGetPosQuery({})
  const [triggerPoDetail] = useLazyGetPoDetailQuery()

  const [createRequest, { isLoading: creating }] =
    useCreateProcurementRequestMutation()
  const [submitRequest, { isLoading: submitting }] =
    useSubmitProcurementRequestMutation()
  const [uploadDocument] = useUploadProcurementDocumentMutation()

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )
  const departmentOptions = useMemo(
    () => (departments || []).map((d) => ({ value: d.id, label: d.name })),
    [departments]
  )
  const poOptions = useMemo(
    () => (pos || []).map((p) => ({ value: p.code, label: p.code, raw: p })),
    [pos]
  )

  const handlePoSelect = async (option) => {
    if (!option) {
      setHeader((prev) => ({
        ...prev,
        poCode: "",
        poTotalAmount: 0,
        poAllowedSupplierIds: [],
      }))
      return
    }
    const detail = await triggerPoDetail(option.value).unwrap().catch(() => null)
    setHeader((prev) => ({
      ...prev,
      poCode: option.value,
      poTotalAmount: detail?.totalAmount || 0,
      poAllowedSupplierIds: detail?.supplierIds || [],
    }))
  }

  const persistDocs = async (requestId) => {
    const entries = Object.entries(pendingDocs)
    for (const [docType, file] of entries) {
      if (!file) continue
      await uploadDocument({ id: requestId, file, docType }).unwrap().catch(() => null)
    }
  }

  const handleSaveDraft = async () => {
    if (!isValid) return
    try {
      const res = await createRequest(buildBody()).unwrap()
      if (!res?.id) {
        showToastError("Failed to save draft: no ID returned.")
        return
      }
      await persistDocs(res.id)
      showToastSuccess("Draft saved successfully.")
      navigate(`/procurement/requests/${res.id}`)
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || "Failed to save draft."
      showToastError(msg)
    }
  }

  const handleSubmit = async () => {
    if (!isValid) return
    try {
      const res = await createRequest(buildBody()).unwrap()
      if (!res?.id) {
        showToastError("Failed to create request: no ID returned.")
        return
      }
      await persistDocs(res.id)
      await submitRequest(res.id).unwrap()
      showToastSuccess("Request submitted successfully.")
      navigate(`/procurement/requests/${res.id}`)
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || "Failed to submit request."
      showToastError(msg)
    }
  }

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <MDTypography variant="h4">Create Purchase Request</MDTypography>
        <MDTypography variant="caption" color="text" mb={2} display="block">
          Procurement › Purchase Requisition › New
        </MDTypography>

        {/* Header block */}
        <SectionCard title="Request Details" icon="description" mb={2}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                labelType="non-sticky"
                labelText="Request Date"
                value={new Date().toISOString().slice(0, 10)}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Requester Region"
                error={!!errors.requesterRegion}
                options={REQUESTER_REGION_OPTIONS}
                value={
                  REQUESTER_REGION_OPTIONS.find(
                    (o) => o.value === header.requesterRegion
                  ) || null
                }
                onChange={(o) => setHeaderField("requesterRegion", o?.value || "")}
              />
              {errors.requesterRegion && (
                <MDTypography variant="caption" color="error">
                  {errors.requesterRegion}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="PR Region"
                error={!!errors.region}
                options={REGION_OPTIONS}
                value={REGION_OPTIONS.find((o) => o.value === header.region) || null}
                onChange={(o) => setHeaderField("region", o?.value || "")}
              />
              {errors.region && (
                <MDTypography variant="caption" color="error">
                  {errors.region}
                </MDTypography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Category"
                error={!!errors.budgetCategoryId}
                options={categoryOptions}
                value={
                  categoryOptions.find((o) => o.value === header.budgetCategoryId) ||
                  null
                }
                onChange={(o) => setHeaderField("budgetCategoryId", o?.value || null)}
              />
              {errors.budgetCategoryId && (
                <MDTypography variant="caption" color="error">
                  {errors.budgetCategoryId}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Department"
                error={!!errors.departmentId}
                options={departmentOptions}
                value={
                  departmentOptions.find((o) => o.value === header.departmentId) ||
                  null
                }
                onChange={(o) => setHeaderField("departmentId", o?.value || null)}
              />
              {errors.departmentId && (
                <MDTypography variant="caption" color="error">
                  {errors.departmentId}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Payment method"
                options={PAYMENT_METHOD_OPTIONS}
                value={
                  PAYMENT_METHOD_OPTIONS.find(
                    (o) => o.value === header.paymentMethod
                  ) || null
                }
                onChange={(o) => setHeaderField("paymentMethod", o?.value || "")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Urgency"
                options={URGENCY_OPTIONS}
                value={URGENCY_OPTIONS.find((o) => o.value === header.urgency) || null}
                onChange={(o) => setHeaderField("urgency", o?.value || "")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDNumberInput
                fullWidth
                labelType="non-sticky"
                labelText="Advance Amount"
                value={header.advanceAmount}
                onChange={(v) => setHeaderField("advanceAmount", v)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDSelect
                isClearable
                labelType="non-sticky"
                labelText="Link with PO"
                options={poOptions}
                value={poOptions.find((o) => o.value === header.poCode) || null}
                onChange={handlePoSelect}
              />
            </Grid>

            <Grid item xs={12}>
              <MDInput
                fullWidth
                multiline
                rows={3}
                required
                labelType="non-sticky"
                labelText="Reason"
                value={header.reason}
                onChange={(e) => setHeaderField("reason", e.target.value)}
                error={!!errors.reason}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                multiline
                rows={2}
                labelType="non-sticky"
                labelText="Allocation Info"
                value={header.allocationInfo}
                onChange={(e) => setHeaderField("allocationInfo", e.target.value)}
              />
            </Grid>
          </Grid>
        </SectionCard>

        {/* Attachments */}
        <SectionCard title="Attached Documents" icon="attach_file" mb={2}>
          {DOC_TYPES.map((d) => (
            <AttachmentSlot
              key={d.value}
              label={d.label}
              docType={d.value}
              file={pendingDocs[d.value]}
              onSelect={({ docType, file }) =>
                setPendingDocs((prev) => ({ ...prev, [docType]: file }))
              }
            />
          ))}
        </SectionCard>

        {/* Payment lines */}
        <SectionCard
          title="Payment Detail"
          icon="payments"
          mb={2}
          action={
            <MDButton size="small" variant="outlined" color="info" onClick={addLine}>
              + Add new line
            </MDButton>
          }
        >
          {lines.map((line, idx) => (
            <PaymentLineRow
              key={idx}
              idx={idx}
              line={line}
              errors={errors.lineErrors?.[idx]}
              suppliers={
                header.poCode && header.poAllowedSupplierIds.length > 0
                  ? (suppliers || []).filter((s) =>
                      header.poAllowedSupplierIds.includes(s.id)
                    )
                  : suppliers
              }
              onUpdate={updateLine}
              onDuplicate={duplicateLine}
              onRemove={removeLine}
            />
          ))}
        </SectionCard>

        {/* Totals */}
        <SectionCard title="Totals" icon="calculate" mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <MDTypography variant="caption" color="text" display="block">
                Total request amount (PR)
              </MDTypography>
              <MDTypography variant="h5">{totalAmountPR.toLocaleString()}</MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDTypography variant="caption" color="text" display="block">
                Total order amount (PO)
              </MDTypography>
              <MDTypography variant="h5">
                {header.poCode ? totalAmountPO.toLocaleString() : "—"}
              </MDTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <MDTypography variant="caption" color="text" display="block">
                Difference
              </MDTypography>
              <MDTypography variant="h5" color={totalsDiffer ? "error" : "text"}>
                {header.poCode ? (totalAmountPR - totalAmountPO).toLocaleString() : "—"}
              </MDTypography>
            </Grid>
            {totalsDiffer && (
              <Grid item xs={12}>
                <MDInput
                  fullWidth
                  multiline
                  rows={2}
                  required
                  labelType="non-sticky"
                  labelText="Explain the difference"
                  value={header.amountDifferencesExplanation}
                  onChange={(e) =>
                    setHeaderField("amountDifferencesExplanation", e.target.value)
                  }
                  error={!!errors.amountDifferencesExplanation}
                />
                {errors.amountDifferencesExplanation && (
                  <MDTypography variant="caption" color="error">
                    {errors.amountDifferencesExplanation}
                  </MDTypography>
                )}
              </Grid>
            )}
          </Grid>
        </SectionCard>

        <MDBox display="flex" gap={2} justifyContent="flex-end">
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/procurement/requests")}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            disabled={!isValid || creating}
            onClick={handleSaveDraft}
          >
            Save draft
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!isValid || creating || submitting}
            onClick={handleSubmit}
          >
            Submit for Approval
          </MDButton>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  )
}

export default CreateRequestContainer
